// ClientInfoModal.jsx
import React from 'react';
import JMJButton from '../JMJButton';
import { differenceInYears, differenceInCalendarDays, parse } from 'date-fns';
import { formatTime } from '../../helpers/datesAndTimes';

const idsToAddresses = {
  0: "JMJ Orlando Clinic",
  1: "JMJ Kissimmee Clinic"
};

const parseDob = (dobStr) => {
  if (!dobStr) return null;
  // Try MM/dd/yyyy first
  const p1 = parse(dobStr, 'MM/dd/yyyy', new Date());
  if (!isNaN(p1)) return p1;
  // Fallback to native Date parsing
  const p2 = new Date(dobStr);
  if (!isNaN(p2)) return p2;
  return null;
};

const getAgeFromDob = (dobStr) => {
  const dob = parseDob(dobStr);
  if (!dob) return null;
  return differenceInYears(new Date(), dob);
};

const getGestationWeeksFromLMP = (lmpStr) => {
  const lmp = parseDob(lmpStr);
  if (!lmp) return null;

  const today = new Date();
  const days = differenceInCalendarDays(today, lmp);
  if (days < 0) return null;

  const fullWeeks = Math.floor(days / 7);
  const remainingDays = days % 7;
  return Number(`${fullWeeks}.${remainingDays}`);
};

const ClientInfoModal = ({
  clientForm,
  setClientForm,
  onSave,
  onClose,

  // NEW: show latest appointment + unconfirmed state
  latestAppointment,
  isApptUnconfirmed,
}) => {
  const age = clientForm.dob ? getAgeFromDob(clientForm.dob) : null;
  const gestationWeeks = clientForm.menstrual
    ? getGestationWeeksFromLMP(clientForm.menstrual)
    : null;

  const hasLatest = !!latestAppointment;
  const latestUnconfirmed =
    hasLatest && typeof isApptUnconfirmed === 'function'
      ? isApptUnconfirmed(latestAppointment)
      : false;

  const latestConfirmedText = hasLatest ? (latestUnconfirmed ? 'No' : 'Yes') : 'N/A';

  const latestLocationName = hasLatest
    ? (idsToAddresses[Number(latestAppointment.location)] || `Location ${latestAppointment.location}`)
    : 'N/A';

  const latestTimeText = hasLatest
    ? `${formatTime(latestAppointment.startTime)} - ${formatTime(latestAppointment.endTime)}`
    : 'N/A';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white p-6 rounded shadow-lg w-[90%] max-w-xl text-black">
        <h2 className="text-2xl font-bold mb-4">Client Info</h2>

        {/* Latest appointment summary */}
        <div className="mb-4">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-semibold">Latest Appointment</div>
            {latestUnconfirmed && (
              <span className="inline-flex items-center px-2 py-0.5 rounded bg-yellow-200 text-black font-extrabold text-[11px] uppercase">
                Unconfirmed
              </span>
            )}
          </div>

          <div className="mt-2 grid grid-cols-2 gap-3">
            <div className="bg-gray-100 border border-gray-300 rounded px-2 py-2 text-sm col-span-2">
              {hasLatest ? (
                <>
                  <div><strong>Date:</strong> {latestAppointment.date || '—'}</div>
                  <div><strong>Time:</strong> {latestTimeText}</div>
                  <div><strong>Location:</strong> {latestLocationName}</div>
                  <div><strong>Confirmed:</strong> {latestConfirmedText}</div>
                </>
              ) : (
                <div className="text-sm">No appointments found for this client.</div>
              )}
            </div>
          </div>

          {latestUnconfirmed && (
            <div className="mt-3 rounded bg-yellow-100 text-yellow-900 font-bold text-center p-2">
              UNCONFIRMED — waiting for client text verification
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          {/* First / Last Name */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold">First Name</label>
            <div className="bg-gray-100 border border-gray-300 rounded px-2 py-1 text-sm">
              {clientForm.firstName || '—'}
            </div>
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-semibold">Last Name</label>
            <div className="bg-gray-100 border border-gray-300 rounded px-2 py-1 text-sm">
              {clientForm.lastName || '—'}
            </div>
          </div>

          {/* Phone / Email */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold">Phone</label>
            <div className="bg-gray-100 border border-gray-300 rounded px-2 py-1 text-sm">
              {clientForm.phone || '—'}
            </div>
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-semibold">Email</label>
            <div className="bg-gray-100 border border-gray-300 rounded px-2 py-1 text-sm">
              {clientForm.email || '—'}
            </div>
          </div>

          {/* DOB with age */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold">DOB</label>
            <div className="bg-gray-100 border border-gray-300 rounded px-2 py-1 text-sm">
              {clientForm.dob || '—'}
              {age !== null && clientForm.dob && (
                <> ({age} years old)</>
              )}
            </div>
          </div>

          {/* LMP with gestation + red star if > 6.0 */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold">LMP</label>
            <div className="bg-gray-100 border border-gray-300 rounded px-2 py-1 text-sm">
              {clientForm.menstrual || '—'}
              {gestationWeeks !== null && clientForm.menstrual && (
                <> (gestation {gestationWeeks.toFixed(1)})</>
              )}
              {gestationWeeks !== null && gestationWeeks > 6 && (
                <span className="text-red-600 ml-1">★</span>
              )}
            </div>
          </div>

          {/* Language (editable) */}
          <div className="flex flex-col col-span-2">
            <label className="text-sm font-semibold">Language</label>
            <select
              value={clientForm.language}
              onChange={e =>
                setClientForm(prev => ({ ...prev, language: e.target.value }))
              }
              className="border border-gray-300 rounded px-2 py-1"
            >
              <option value="English">English</option>
              <option value="Spanish">Spanish</option>
            </select>
          </div>

          {/* Admin Notes */}
          <div className="flex flex-col col-span-2">
            <label className="text-sm font-semibold">Admin Notes</label>
            <textarea
              value={clientForm.adminNotes}
              onChange={e =>
                setClientForm(prev => ({ ...prev, adminNotes: e.target.value }))
              }
              className="border border-gray-300 rounded px-2 py-1"
            />
          </div>

          {/* Client Note (read-only) */}
          <div className="flex flex-col col-span-2">
            <label className="text-sm font-semibold">Client Note</label>
            <textarea
              value={clientForm.notes}
              readOnly
              className="border border-gray-300 rounded px-2 py-1 bg-gray-100"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <JMJButton text="Close" onClick={onClose} color="gray" />
          <JMJButton text="Save" onClick={onSave} color="green" />
        </div>
      </div>
    </div>
  );
};

export default ClientInfoModal;
